import { useState, useEffect, useRef } from 'react';
import { services } from '../../../mocks/services';

type CategoryType = 'all' | 'washing' | 'dishwasher' | 'dryer';

export default function Services() {
  const [activeCategory, setActiveCategory] = useState<CategoryType>('all');
  const [isVisible, setIsVisible] = useState(false);
  const [cardsVisible, setCardsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const [statsVisible, setStatsVisible] = useState(false);
  const [counters, setCounters] = useState({ repairs: 0, satisfaction: 0, years: 0 });

  const categories = [
    { id: 'all' as CategoryType, name: 'Все', fullName: 'Все услуги', icon: 'ri-apps-line' },
    { id: 'washing' as CategoryType, name: 'Стиральные', fullName: 'Стиральные', icon: 'ri-loader-2-line' },
    { id: 'dishwasher' as CategoryType, name: 'Посудомоечные', fullName: 'Посудомоечные', icon: 'ri-drop-line' },
    { id: 'dryer' as CategoryType, name: 'Сушильные', fullName: 'Сушильные', icon: 'ri-windy-line' },
  ];

  const filteredServices = services.filter(
    (service) =>
      activeCategory === 'all' ||
      service.category === activeCategory ||
      service.category === 'all'
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setCardsVisible(true); },
      { threshold: 0.05 }
    );
    if (cardsRef.current) observer.observe(cardsRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !statsVisible) {
          setStatsVisible(true);
          animateCounters();
        }
      },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, [statsVisible]);

  const animateCounters = () => {
    const duration = 2000;
    const steps = 60;
    const targets = { repairs: 500, satisfaction: 98, years: 5 };
    let currentStep = 0;
    const interval = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      setCounters({
        repairs: Math.floor(targets.repairs * progress),
        satisfaction: Math.floor(targets.satisfaction * progress),
        years: Math.floor(targets.years * progress),
      });
      if (currentStep >= steps) {
        clearInterval(interval);
        setCounters(targets);
      }
    }, duration / steps);
  };

  return (
    <section
      id="services"
      className="py-14 md:py-20 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden"
    >
      <div className="absolute top-20 left-10 w-48 md:w-72 h-48 md:h-72 bg-teal-200/30 rounded-full blur-3xl animate-float"></div>
      <div className="absolute bottom-20 right-10 w-64 md:w-96 h-64 md:h-96 bg-orange-200/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" ref={sectionRef}>
        {/* Header — slides down */}
        <div className={`text-center mb-10 md:mb-16 transition-all duration-800 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-12'}`}>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-3 md:mb-4">
            Наши услуги
          </h2>
          <div className={`h-1 mx-auto rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 transition-all duration-1000 delay-300 ${isVisible ? 'w-24' : 'w-0'}`}></div>
          <p className={`text-sm md:text-lg text-gray-600 max-w-2xl mx-auto mt-3 md:mt-4 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            Профессиональный ремонт и обслуживание стиральных, посудомоечных и сушильных машин
          </p>
        </div>

        {/* Category Filter — slides from left */}
        <div className={`flex justify-start md:justify-center gap-2 md:gap-3 mb-8 md:mb-12 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-16'}`}>
          {categories.map((category, idx) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`flex items-center gap-1.5 md:gap-2 px-4 md:px-6 py-2.5 md:py-3 rounded-full font-semibold text-xs md:text-sm transition-all duration-300 whitespace-nowrap cursor-pointer flex-shrink-0 ${
                activeCategory === category.id
                  ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white shadow-lg shadow-teal-500/30 scale-105'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 hover:border-teal-300'
              }`}
              style={{ transitionDelay: `${400 + idx * 80}ms` }}
            >
              <i className={`${category.icon} text-base md:text-lg`}></i>
              <span className="md:hidden">{category.name}</span>
              <span className="hidden md:inline">{category.fullName}</span>
            </button>
          ))}
        </div>

        {/* Services Grid — cards fly in from alternating sides */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8" ref={cardsRef}>
          {filteredServices.map((service, index) => (
            <div
              key={`${activeCategory}-${index}`}
              className={`group relative bg-white rounded-xl md:rounded-2xl p-5 md:p-8 transition-all duration-700 hover:shadow-2xl hover:-translate-y-1 md:hover:-translate-y-2 cursor-pointer border border-gray-100 ${
                cardsVisible
                  ? 'opacity-100 translate-y-0 translate-x-0 rotate-0'
                  : index % 3 === 0
                    ? 'opacity-0 -translate-x-16 rotate-2'
                    : index % 3 === 1
                      ? 'opacity-0 translate-y-16'
                      : 'opacity-0 translate-x-16 -rotate-2'
              }`}
              style={{ transitionDelay: `${index * 120}ms`, transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
            >
              <div className="absolute inset-0 rounded-xl md:rounded-2xl bg-gradient-to-br from-teal-400 via-emerald-400 to-teal-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl"></div>

              <div className="relative z-10">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg shadow-teal-500/30">
                  <i className={`${service.icon} text-xl md:text-3xl text-white`}></i>
                </div>

                <h3 className="text-base md:text-xl font-bold text-gray-900 mb-2 md:mb-3 group-hover:text-teal-600 transition-colors">
                  {service.title}
                </h3>
                <p className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4 leading-relaxed line-clamp-3 md:line-clamp-none">
                  {service.description}
                </p>

                <div className="flex items-center text-teal-600 font-semibold text-xs md:text-sm group-hover:gap-2 md:group-hover:gap-3 transition-all">
                  <span>Подробнее</span>
                  <i className="ri-arrow-right-line ml-1.5 md:ml-2 group-hover:translate-x-2 transition-transform"></i>
                </div>
              </div>

              <div className="absolute inset-0 rounded-xl md:rounded-2xl overflow-hidden">
                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
